import React, { useState, useEffect, useMemo, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  CssBaseline,
  Backdrop,
  CircularProgress,
  Avatar,
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  Divider,
  Tab,
  Tabs,
  Chip,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  Stack,
  Container,
  Fade,
  Zoom,
  LinearProgress,
  Collapse,
} from "@mui/material";
import { useTheme, alpha, keyframes } from "@mui/material/styles";
import {
  Business as BusinessIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  Edit as EditIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  Campaign as CampaignIcon,
  Note as NoteIcon,
  CheckCircle as CheckCircleIcon,
  CalendarToday as CalendarIcon,
  TrendingUp as TrendingUpIcon,
  AttachMoney as MoneyIcon,
  Assignment as AssignmentIcon,
  Add as AddIcon,
  Launch as LaunchIcon,
  LocalOffer as TagIcon,
  Lock as LockIcon,
  EmojiEvents as TrophyIcon,
  Bolt as BoltIcon,
  Share as ShareIcon,
  ContentCopy as CopyIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CheckCircleOutline as CheckIcon,
  RadioButtonUnchecked as UncheckedIcon,
  Person as PersonIcon,
  Link as LinkIcon,
  Verified as VerifiedIcon,
  ShoppingBag as ShoppingBagIcon,
  LocalMall as LocalMallIcon,
} from "@mui/icons-material";
import { toast } from "sonner";
import API from "../../../API";
import HomeHeader from "../../../Components/HomeHeader";
import ClientPopups from "./components/clientpopups";
import useAuth from "../../../Hooks/use-auth";
import { useNavigate as useNav } from "react-router-dom";
import routes from "../../../Config/routes";

// ─── Animations ───────────────────────────────────────────────────────────────

const shimmer = keyframes`
  0% { background-position: -200% center; }
  100% { background-position: 200% center; }
`;

const pulseGold = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(245, 158, 11, 0.4); }
  50% { box-shadow: 0 0 0 12px rgba(245, 158, 11, 0); }
`;

const countUp = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
`;

const scanline = keyframes`
  0% { top: -100%; }
  100% { top: 200%; }
`;

// ─── Bounty Calculation Engine ────────────────────────────────────────────────

/**
 * Calculates a bounty reward and quality score for a client
 * based on the richness & completeness of their profile data.
 *
 * Returns:
 *   { bountyUSD, transactionPct, qualityScore, maxScore, signals }
 */
export function calculateClientBounty(client) {
  if (!client) return { bountyUSD: 0, transactionPct: 0, qualityScore: 0, maxScore: 100, signals: [] };

  const signals = [];
  let score = 0;

  // ── Identity completeness ──────────────────────────────────────
  const hasCompany = Boolean(client.company?.trim());
  const hasPhone   = Boolean(client.phone_number?.trim());
  const hasEmail   = (() => {
    const e = client.email?.trim().toLowerCase();
    return e && e !== 'n/a' && e.includes('@');
  })();
  const hasLocation = Boolean(client.location?.trim());
  const hasBio      = Boolean(client.bio?.trim());

  if (hasCompany) {
    const len  = client.company.trim().length;
    const pts  = len > 3 ? 18 : 8;
    score += pts;
    signals.push({ label: "Company name", pts, hit: true });
  } else {
    signals.push({ label: "Company name", pts: 18, hit: false });
  }

  if (hasPhone) {
    score += 22;
    signals.push({ label: "Phone number", pts: 22, hit: true });
  } else {
    signals.push({ label: "Phone number", pts: 22, hit: false });
  }

  if (hasEmail) {
    score += 12;
    signals.push({ label: "Valid email", pts: 12, hit: true });
  } else {
    signals.push({ label: "Valid email", pts: 12, hit: false });
  }

  if (hasLocation) {
    score += 6;
    signals.push({ label: "Location", pts: 6, hit: true });
  } else {
    signals.push({ label: "Location", pts: 6, hit: false });
  }

  if (hasBio) {
    score += 5;
    signals.push({ label: "Bio / description", pts: 5, hit: true });
  } else {
    signals.push({ label: "Bio / description", pts: 5, hit: false });
  }

  // ── Connection & verification ──────────────────────────────────
  if (client.is_connected) {
    score += 15;
    signals.push({ label: "Connected to Blitz", pts: 15, hit: true });
  } else {
    signals.push({ label: "Connected to Blitz", pts: 15, hit: false });
  }

  // ── Activity depth ─────────────────────────────────────────────
  const campaignCount = client.mapped_campaigns?.length || 0;
  const campaignPts   = Math.min(campaignCount * 3, 12);
  if (campaignCount > 0) {
    score += campaignPts;
    signals.push({ label: `${campaignCount} campaign${campaignCount !== 1 ? 's' : ''}`, pts: campaignPts, hit: true });
  } else {
    signals.push({ label: "Campaigns", pts: 12, hit: false });
  }

  const noteCount = client.note_count || 0;
  const notePts   = Math.min(noteCount * 2, 6);
  if (noteCount > 0) {
    score += notePts;
    signals.push({ label: `${noteCount} note${noteCount !== 1 ? 's' : ''}`, pts: notePts, hit: true });
  } else {
    signals.push({ label: "Notes", pts: 6, hit: false });
  }

  // ── Tags (data richness) ───────────────────────────────────────
  const tagCount = (client.tags || []).length;
  const tagPts   = Math.min(tagCount * 1, 4);
  if (tagCount > 0) {
    score += tagPts;
    signals.push({ label: `${tagCount} tag${tagCount !== 1 ? 's' : ''}`, pts: tagPts, hit: true });
  } else {
    signals.push({ label: "Tags", pts: 4, hit: false });
  }

  const maxScore = 100;
  score = Math.min(score, maxScore);
  const pct = score / maxScore; // 0 – 1

  // ── Dollar bounty: $10 base → $350 max, exponential-ish curve ─
  const bountyUSD = Math.round(10 + pct * pct * 340);

  // ── Transaction bonus: 0.25% → 3.5% ──────────────────────────
  const transactionPct = parseFloat((0.25 + pct * 3.25).toFixed(2));

  return { bountyUSD, transactionPct, qualityScore: score, maxScore, signals };
}

// ─── Tier helper ──────────────────────────────────────────────────────────────

function getBountyTier(score, maxScore) {
  const pct = score / maxScore;
  if (pct >= 0.85) return { label: "ELITE",    color: "#F59E0B", bg: "#78350F", glow: "#F59E0B" };
  if (pct >= 0.65) return { label: "PREMIUM",  color: "#C084FC", bg: "#4C1D95", glow: "#A855F7" };
  if (pct >= 0.45) return { label: "VALUED",   color: "#34D399", bg: "#064E3B", glow: "#10B981" };
  if (pct >= 0.25) return { label: "STANDARD", color: "#60A5FA", bg: "#1E3A5F", glow: "#3B82F6" };
  return               { label: "BASIC",    color: "#9CA3AF", bg: "#374151", glow: "#6B7280" };
}

// ─── Animated counter ─────────────────────────────────────────────────────────

function AnimatedNumber({ value, prefix = "", suffix = "", duration = 1200 }) {
  const [display, setDisplay] = useState(0);
  const rafRef = useRef(null);

  useEffect(() => {
    const start = Date.now();
    const animate = () => {
      const elapsed = Date.now() - start;
      const progress = Math.min(elapsed / duration, 1);
      const ease = 1 - Math.pow(1 - progress, 3);
      setDisplay(Math.round(ease * value));
      if (progress < 1) rafRef.current = requestAnimationFrame(animate);
    };
    rafRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(rafRef.current);
  }, [value, duration]);

  return <>{prefix}{display.toLocaleString()}{suffix}</>;
}

// ─── BountyCard ──────────────────────────────────────────────────────────────

function BountyCard({ client, isLoggedIn, onShareBounty }) {
  const theme   = useTheme();
  const isDark  = theme.palette.mode === "dark";
  const navigate = useNav();
  const [expanded, setExpanded] = useState(false);
  const [copied,   setCopied]   = useState(false);

  const { bountyUSD, transactionPct, qualityScore, maxScore, signals } =
    useMemo(() => calculateClientBounty(client), [client]);

  const tier = getBountyTier(qualityScore, maxScore);
  const qualityPct = (qualityScore / maxScore) * 100;

  const handleCopyBountyLink = () => {
    const url = `${window.location.origin}/client/${client?.client_id || client?.id}`;
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success("Bounty link copied!");
    });
  };

  // ── Locked state (not authenticated) ─────────────────────────
  if (!isLoggedIn) {
    return (
      <Paper
        elevation={0}
        sx={{
          borderRadius: 4,
          overflow: "hidden",
          border: `1px solid ${alpha("#F59E0B", 0.3)}`,
          mb: 4,
          position: "relative",
        }}
      >
        {/* Header */}
        <Box sx={{
          px: 3, py: 2,
          background: "linear-gradient(90deg, #78350F 0%, #92400E 100%)",
          display: "flex", alignItems: "center", gap: 1.5,
        }}>
          <TrophyIcon sx={{ color: "#FCD34D", fontSize: 22 }} />
          <Typography fontWeight={800} sx={{ color: "#FCD34D", letterSpacing: 1, fontSize: "0.85rem", textTransform: "uppercase" }}>
            Referral Bounty
          </Typography>
        </Box>

        {/* Blurred / locked body */}
        <Box sx={{
          position: "relative",
          backgroundColor: isDark ? "#1C1917" : "#FFFBEB",
          px: 3, py: 4,
          display: "flex", flexDirection: "column", alignItems: "center", gap: 2,
        }}>
          {/* Ghost numbers */}
          <Box sx={{ filter: "blur(8px)", userSelect: "none", textAlign: "center" }}>
            <Typography variant="h2" fontWeight={900} sx={{ color: "#F59E0B" }}>$???</Typography>
            <Typography variant="h6" fontWeight={600} sx={{ color: "#78350F" }}>+?.??% per transaction</Typography>
          </Box>
          {/* Lock overlay */}
          <Box sx={{
            position: "absolute", inset: 0,
            display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
            gap: 1.5,
            backgroundColor: alpha(isDark ? "#1C1917" : "#FFFBEB", 0.7),
            backdropFilter: "blur(2px)",
          }}>
            <Box sx={{
              width: 56, height: 56, borderRadius: "50%",
              backgroundColor: alpha("#F59E0B", 0.15),
              border: `2px solid ${alpha("#F59E0B", 0.4)}`,
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <LockIcon sx={{ color: "#F59E0B", fontSize: 26 }} />
            </Box>
            <Typography fontWeight={700} sx={{ color: "#92400E" }}>Bounty • Login to see</Typography>
            <Button
              variant="contained"
              size="small"
              onClick={() => navigate(routes.login)}
              sx={{
                background: "linear-gradient(135deg, #F59E0B, #D97706)",
                color: "#1C1917",
                fontWeight: 700,
                borderRadius: 8,
                textTransform: "none",
                px: 3,
              }}
            >
              Log in to reveal
            </Button>
          </Box>
        </Box>
      </Paper>
    );
  }

  // ── Authenticated: full bounty reveal ────────────────────────

  return (
    <Zoom in timeout={600}>
      <Paper
        elevation={0}
        sx={{
          borderRadius: 4,
          overflow: "hidden",
          border: `2px solid ${alpha(tier.color, 0.5)}`,
          mb: 4,
          position: "relative",
          animation: `${pulseGold} 3s ease-in-out infinite`,
        }}
      >
        {/* Header bar */}
        <Box sx={{
          px: 3, py: 2,
          background: `linear-gradient(90deg, ${tier.bg} 0%, ${alpha(tier.bg, 0.7)} 100%)`,
          display: "flex", alignItems: "center", justifyContent: "space-between",
        }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <TrophyIcon sx={{ color: tier.color, fontSize: 22 }} />
            <Typography fontWeight={800} sx={{
              color: tier.color, letterSpacing: 1.5, fontSize: "0.8rem", textTransform: "uppercase",
            }}>
              Referral Bounty
            </Typography>
            <Chip
              label={tier.label}
              size="small"
              sx={{
                height: 20, fontSize: "0.65rem", fontWeight: 800,
                backgroundColor: alpha(tier.color, 0.2),
                border: `1px solid ${alpha(tier.color, 0.5)}`,
                color: tier.color,
              }}
            />
          </Box>
          <Box sx={{ display: "flex", gap: 0.5 }}>
            <Tooltip title={copied ? "Copied!" : "Copy bounty link"}>
              <IconButton size="small" onClick={handleCopyBountyLink}
                sx={{ color: tier.color, '&:hover': { backgroundColor: alpha(tier.color, 0.1) } }}>
                {copied ? <CheckIcon fontSize="small" /> : <CopyIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Main body */}
        <Box sx={{
          backgroundColor: isDark ? "#1C1917" : "#FFFBEB",
          px: { xs: 2, sm: 4 }, py: 3,
          position: "relative",
          overflow: "hidden",
        }}>
          {/* Scanline decoration */}
          <Box sx={{
            position: "absolute",
            left: 0, right: 0,
            height: "2px",
            background: `linear-gradient(90deg, transparent, ${alpha(tier.color, 0.3)}, transparent)`,
            animation: `${scanline} 4s linear infinite`,
            pointerEvents: "none",
          }} />

          {/* Shimmer background text */}
          <Typography sx={{
            position: "absolute",
            top: "50%", left: "50%",
            transform: "translate(-50%, -50%)",
            fontSize: { xs: "5rem", sm: "8rem" },
            fontWeight: 900,
            color: alpha(tier.color, 0.04),
            whiteSpace: "nowrap",
            userSelect: "none",
            pointerEvents: "none",
          }}>
            BOUNTY
          </Typography>

          <Grid container spacing={3} alignItems="center">
            {/* Dollar amount */}
            <Grid item xs={12} sm={6}>
              <Box sx={{ textAlign: { xs: "center", sm: "left" } }}>
                <Typography variant="caption" sx={{
                  color: alpha(tier.color, 0.7), fontWeight: 700,
                  letterSpacing: 2, textTransform: "uppercase", fontSize: "0.7rem",
                }}>
                  Referral Reward
                </Typography>
                <Typography variant="h2" fontWeight={900} sx={{
                  color: tier.color,
                  fontFamily: '"Courier New", monospace',
                  lineHeight: 1,
                  mt: 0.5,
                  animation: `${countUp} 0.5s ease`,
                  textShadow: `0 0 40px ${alpha(tier.color, 0.4)}`,
                }}>
                  <AnimatedNumber value={bountyUSD} prefix="$" />
                </Typography>
                <Typography variant="body2" sx={{ color: isDark ? "#D97706" : "#92400E", fontWeight: 600, mt: 0.5 }}>
                  paid when this client transacts
                </Typography>
              </Box>
            </Grid>

            {/* Transaction % */}
            <Grid item xs={12} sm={6}>
              <Box sx={{
                textAlign: { xs: "center", sm: "right" },
              }}>
                <Typography variant="caption" sx={{
                  color: alpha(tier.color, 0.7), fontWeight: 700,
                  letterSpacing: 2, textTransform: "uppercase", fontSize: "0.7rem",
                }}>
                  Revenue Share
                </Typography>
                <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.5, justifyContent: { xs: "center", sm: "flex-end" }, mt: 0.5 }}>
                  <Typography variant="h3" fontWeight={900} sx={{
                    color: tier.color, fontFamily: '"Courier New", monospace', lineHeight: 1,
                    textShadow: `0 0 40px ${alpha(tier.color, 0.3)}`,
                  }}>
                    +<AnimatedNumber value={Math.round(transactionPct * 100)} /><span style={{ fontSize: "1.2rem" }}>%</span>
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ color: isDark ? "#D97706" : "#92400E", fontWeight: 600, mt: 0.5 }}>
                  on all transactions processed
                </Typography>
              </Box>
            </Grid>
          </Grid>

          {/* Quality score bar */}
          <Box sx={{ mt: 3 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.75 }}>
              <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600, letterSpacing: 0.5, textTransform: "uppercase", fontSize: "0.65rem" }}>
                Client quality score
              </Typography>
              <Typography variant="caption" sx={{ color: tier.color, fontWeight: 800, fontFamily: '"Courier New", monospace' }}>
                {qualityScore} / {maxScore}
              </Typography>
            </Box>
            <Box sx={{ position: "relative", height: 8, borderRadius: 4, backgroundColor: alpha(tier.color, 0.1), overflow: "hidden" }}>
              <Box sx={{
                position: "absolute", left: 0, top: 0, bottom: 0,
                width: `${qualityPct}%`,
                borderRadius: 4,
                background: `linear-gradient(90deg, ${tier.color}, ${alpha(tier.color, 0.6)})`,
                backgroundSize: "200% 100%",
                animation: `${shimmer} 2s linear infinite`,
                transition: "width 1s cubic-bezier(0.34, 1.56, 0.64, 1)",
              }} />
            </Box>
          </Box>

          {/* Breakdown toggle */}
          <Box
            onClick={() => setExpanded(p => !p)}
            sx={{
              display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5,
              mt: 2, cursor: "pointer", opacity: 0.6,
              "&:hover": { opacity: 1 },
              transition: "opacity 0.2s",
            }}
          >
            <Typography variant="caption" sx={{ fontWeight: 600, color: tier.color, textTransform: "uppercase", letterSpacing: 1, fontSize: "0.65rem" }}>
              {expanded ? "Hide breakdown" : "Score breakdown"}
            </Typography>
            {expanded ? <ExpandLessIcon sx={{ fontSize: 14, color: tier.color }} /> : <ExpandMoreIcon sx={{ fontSize: 14, color: tier.color }} />}
          </Box>
        </Box>

        {/* Breakdown section */}
        <Collapse in={expanded}>
          <Box sx={{
            backgroundColor: isDark ? "#111827" : "#FEF3C7",
            borderTop: `1px solid ${alpha(tier.color, 0.15)}`,
            px: { xs: 2, sm: 4 }, py: 2.5,
          }}>
            <Typography variant="caption" sx={{
              color: alpha(tier.color, 0.7), fontWeight: 700, letterSpacing: 2,
              textTransform: "uppercase", fontSize: "0.65rem", display: "block", mb: 2,
            }}>
              What drives this bounty
            </Typography>
            <Grid container spacing={1}>
              {signals.map((sig) => (
                <Grid key={sig.label} item xs={12} sm={6}>
                  <Box sx={{
                    display: "flex", alignItems: "center", gap: 1.5,
                    py: 0.75, px: 1.5, borderRadius: 2,
                    backgroundColor: sig.hit
                      ? alpha(tier.color, 0.08)
                      : alpha(isDark ? "#fff" : "#000", 0.03),
                    border: `1px solid ${sig.hit ? alpha(tier.color, 0.2) : alpha(isDark ? "#fff" : "#000", 0.06)}`,
                  }}>
                    {sig.hit
                      ? <CheckIcon sx={{ fontSize: 14, color: tier.color }} />
                      : <UncheckedIcon sx={{ fontSize: 14, color: "text.disabled" }} />
                    }
                    <Typography variant="caption" sx={{
                      flex: 1,
                      color: sig.hit ? "text.primary" : "text.disabled",
                      fontWeight: sig.hit ? 600 : 400,
                    }}>
                      {sig.label}
                    </Typography>
                    <Typography variant="caption" sx={{
                      fontFamily: '"Courier New", monospace',
                      fontWeight: 800,
                      color: sig.hit ? tier.color : "text.disabled",
                    }}>
                      +{sig.pts}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>

            {/* How it works note */}
            <Box sx={{
              mt: 2.5, p: 2, borderRadius: 2,
              backgroundColor: alpha(tier.color, 0.06),
              border: `1px solid ${alpha(tier.color, 0.15)}`,
            }}>
              <Typography variant="caption" sx={{ color: "text.secondary", lineHeight: 1.6 }}>
                <strong style={{ color: tier.color }}>How bounties work:</strong> Share this client's link.
                When they sign up and complete their first transaction through Blitz, you receive the bounty
                reward plus an ongoing revenue share on every future transaction they process.
              </Typography>
            </Box>
          </Box>
        </Collapse>

        {/* Share CTA */}
        <Box sx={{
          px: { xs: 2, sm: 4 }, py: 2,
          backgroundColor: isDark ? "#0C0A09" : "#FFF7ED",
          borderTop: `1px solid ${alpha(tier.color, 0.12)}`,
          display: "flex", gap: 1.5, flexWrap: "wrap",
        }}>
          <Button
            size="small"
            variant="contained"
            startIcon={<ShareIcon fontSize="small" />}
            onClick={handleCopyBountyLink}
            sx={{
              borderRadius: 8, textTransform: "none", fontWeight: 700, flex: 1,
              background: `linear-gradient(135deg, ${tier.color}, ${alpha(tier.color, 0.75)})`,
              color: tier.bg,
              boxShadow: `0 4px 14px ${alpha(tier.color, 0.35)}`,
              "&:hover": { boxShadow: `0 6px 20px ${alpha(tier.color, 0.5)}` },
            }}
          >
            {copied ? "Link copied!" : "Share bounty link"}
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={<BoltIcon fontSize="small" />}
            href={`${window.location.origin}/${routes.myReferrals}`}
            sx={{
              borderRadius: 8, textTransform: "none", fontWeight: 700,
              borderColor: alpha(tier.color, 0.5), color: tier.color,
              "&:hover": { borderColor: tier.color, backgroundColor: alpha(tier.color, 0.08) },
            }}
          >
            My referrals
          </Button>
        </Box>
      </Paper>
    </Zoom>
  );
}

// ─── Existing sub-components (preserved from original) ────────────────────────

const getCampaignStatusColor = (status, isDark) => {
  const colors = {
    Launched:  { light: "#10B981", dark: "#34D399" },
    Completed: { light: "#3B82F6", dark: "#60A5FA" },
    Draft:     { light: "#F59E0B", dark: "#FBBF24" },
    Paused:    { light: "#6B7280", dark: "#9CA3AF" },
  };
  return colors[status]?.[isDark ? "dark" : "light"] || colors.Draft[isDark ? "dark" : "light"];
};

const getOrderStatusColor = (status, isDark) => {
  const colors = {
    fulfilled: { light: "#10B981", dark: "#34D399" },
    paid:      { light: "#10B981", dark: "#34D399" },
    pending:   { light: "#F59E0B", dark: "#FBBF24" },
    refunded:  { light: "#EF4444", dark: "#F87171" },
    imported:  { light: "#6B7280", dark: "#9CA3AF" },
  };
  return colors[status]?.[isDark ? "dark" : "light"] || colors.pending[isDark ? "dark" : "light"];
};

const StatsCard = ({ icon, label, value, color, onClick }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  return (
    <Paper elevation={0} onClick={onClick} sx={{
      p: 2.5, textAlign: "center", borderRadius: 3,
      border: `1px solid ${isDark ? "#2A2A2A" : "#F0F0F0"}`,
      backgroundColor: isDark ? "#1A1A1A" : "#FFFFFF",
      cursor: onClick ? "pointer" : "default",
      transition: "all 0.3s ease",
      "&:hover": {
        transform: onClick ? "translateY(-2px)" : "none",
        boxShadow: onClick ? (isDark ? "0 10px 25px rgba(0,0,0,0.3)" : "0 10px 25px rgba(0,0,0,0.1)") : "none",
      },
    }}>
      <Avatar sx={{ width: 56, height: 56, backgroundColor: `${color}20`, color, mx: "auto", mb: 1.5 }}>
        {icon}
      </Avatar>
      <Typography variant="h4" fontWeight={800} color={color} mb={0.5}>{value}</Typography>
      <Typography variant="body2" color="text.secondary" fontWeight={600}>{label}</Typography>
    </Paper>
  );
};

const CampaignCard = ({ campaign, index, isDark }) => {
  const navigate = useNavigate();
  const statusColor = getCampaignStatusColor(campaign.status, isDark);
  return (
    <Zoom in timeout={300 + index * 100}>
      <Card elevation={0} onClick={() => navigate(`/campaign/${campaign.campaign_id}`)} sx={{
        borderRadius: 3, border: `1px solid ${isDark ? "#2A2A2A" : "#F0F0F0"}`,
        backgroundColor: isDark ? "#1A1A1A" : "#FFFFFF",
        cursor: "pointer", transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        position: "relative", overflow: "visible",
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: isDark ? "0 20px 40px rgba(0,0,0,0.3)" : "0 20px 40px rgba(0,0,0,0.1)",
          borderColor: statusColor,
        },
        "&::before": {
          content: '""', position: "absolute", top: 0, left: 0, right: 0, height: "3px",
          background: `linear-gradient(90deg, ${statusColor}, ${statusColor}80)`,
          borderRadius: "3px 3px 0 0",
        },
      }}>
        <CardContent sx={{ p: 3 }}>
          <Box display="flex" alignItems="flex-start" justifyContent="space-between" mb={2}>
            <Box display="flex" alignItems="center" gap={1.5} flex={1}>
              <Avatar sx={{ width: 40, height: 40, backgroundColor: `${statusColor}20`, color: statusColor }}>
                <CampaignIcon />
              </Avatar>
              <Box flex={1} minWidth={0}>
                <Typography variant="h6" sx={{
                  fontWeight: 700, fontSize: "1.1rem", lineHeight: 1.3, mb: 0.5,
                  overflow: "hidden", textOverflow: "ellipsis",
                  display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
                }}>
                  {campaign.campaign_name}
                </Typography>
                <Chip size="small" label={campaign.status} sx={{
                  backgroundColor: `${statusColor}20`, color: statusColor, fontWeight: 600, fontSize: "0.75rem", height: 24,
                }} />
              </Box>
            </Box>
            <IconButton size="small"><LaunchIcon fontSize="small" /></IconButton>
          </Box>
          <Stack spacing={1}>
            <Box display="flex" alignItems="center" gap={1} color="text.secondary">
              <MoneyIcon fontSize="small" />
              <Typography variant="body2" fontWeight={600}>${campaign.amount?.toLocaleString() || "0"}</Typography>
            </Box>
            {campaign.date_key && (
              <Box display="flex" alignItems="center" gap={1} color="text.secondary">
                <CalendarIcon fontSize="small" />
                <Typography variant="body2">
                  {new Date(campaign.date_key).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                </Typography>
              </Box>
            )}
          </Stack>
        </CardContent>
      </Card>
    </Zoom>
  );
};

const NoteCard = ({ note, index, isDark }) => (
  <Zoom in timeout={300 + index * 100}>
    <Paper elevation={0} sx={{
      p: 3, borderRadius: 3, border: `1px solid ${isDark ? "#2A2A2A" : "#F0F0F0"}`,
      backgroundColor: isDark ? "#1A1A1A" : "#FFFFFF", transition: "all 0.3s ease",
      "&:hover": { borderColor: isDark ? "#404040" : "#E0E0E0" },
    }}>
      <Box display="flex" alignItems="flex-start" gap={2} mb={2}>
        <Avatar sx={{ width: 40, height: 40, backgroundColor: isDark ? "#2A2A2A" : "#F5F5F5", color: isDark ? "#FFFFFF" : "#666666" }}>
          <NoteIcon fontSize="small" />
        </Avatar>
        <Box flex={1}>
          <Typography variant="body1" sx={{ mb: 1, lineHeight: 1.6 }}>{note.body}</Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            {note.category && <Chip size="small" label={note.category} sx={{ height: 20, fontSize: "0.7rem" }} />}
            {note.contact_method && <Chip size="small" label={note.contact_method} sx={{ height: 20, fontSize: "0.7rem" }} />}
          </Stack>
        </Box>
      </Box>
      {note.note_date && (
        <Typography variant="caption" color="text.secondary">
          {new Date(note.note_date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
        </Typography>
      )}
    </Paper>
  </Zoom>
);

const PurchaseCard = ({ purchase, index, isDark }) => {
  const statusColor = getOrderStatusColor(purchase.status, isDark);
  return (
    <Zoom in timeout={300 + index * 100}>
      <Card elevation={0} sx={{
        borderRadius: 3, border: `1px solid ${isDark ? "#2A2A2A" : "#F0F0F0"}`,
        backgroundColor: isDark ? "#1A1A1A" : "#FFFFFF",
        position: "relative", overflow: "visible",
        "&::before": {
          content: '""', position: "absolute", top: 0, left: 0, right: 0, height: "3px",
          background: `linear-gradient(90deg, ${statusColor}, ${statusColor}80)`,
          borderRadius: "3px 3px 0 0",
        },
      }}>
        <CardContent sx={{ p: 3 }}>
          <Box display="flex" alignItems="flex-start" justifyContent="space-between" mb={2}>
            <Box display="flex" alignItems="center" gap={1.5} flex={1} minWidth={0}>
              <Avatar sx={{ width: 40, height: 40, backgroundColor: `${statusColor}20`, color: statusColor }}>
                <ShoppingBagIcon />
              </Avatar>
              <Box flex={1} minWidth={0}>
                <Typography variant="h6" noWrap sx={{ fontWeight: 700, fontSize: "1.05rem", lineHeight: 1.3, mb: 0.5 }}>
                  {purchase.product_name || "Order"}
                </Typography>
                <Chip size="small" label={purchase.status} sx={{
                  backgroundColor: `${statusColor}20`, color: statusColor, fontWeight: 600,
                  fontSize: "0.72rem", height: 22, textTransform: "capitalize",
                }} />
              </Box>
            </Box>
            <Typography variant="h6" fontWeight={800} sx={{ flexShrink: 0, ml: 1 }}>
              ${parseFloat(purchase.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </Typography>
          </Box>
          {purchase.purchased_at && (
            <Box display="flex" alignItems="center" gap={1} color="text.secondary">
              <CalendarIcon fontSize="small" />
              <Typography variant="body2">
                {new Date(purchase.purchased_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>
    </Zoom>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function ClientProfile() {
  const { clientId } = useParams();
  const navigate    = useNavigate();
  const theme       = useTheme();
  const isDark      = theme.palette.mode === "dark";

  const { isAuthorized: userOK }    = useAuth();

  const [client,  setClient]  = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab,     setTab]     = useState(0);
  const [popup,   setPopup]   = useState({ type: null, client: null });
  const [purchases,        setPurchases]        = useState([]);
  const [purchasesLoading, setPurchasesLoading]  = useState(true);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const data = await API.users.getClientDetails(clientId);
        if (mounted) setClient(data);
      } catch (err) {
        toast.error("Failed to load client details");
        console.error(err);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [clientId]);

  // Blitz Commerce order history for this client. No dedicated per-client
  // endpoint exists yet (see docs/cummerce/crm-commerce-mapping.md) — this
  // filters the full purchasers list client-side by client_id, falling back
  // to email match since CommerceOrder.client_id is still a placeholder int
  // on the backend today, not a real FK.
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const all = await API.commerce.fetchPurchasers();
        if (!mounted) return;
        const mine = (all || []).filter((o) =>
          String(o.client_id) === String(clientId) ||
          (client?.email && o.customer_email?.toLowerCase() === client.email.toLowerCase())
        );
        setPurchases(mine);
      } catch (err) {
        // Commerce may not be enabled for this account/company — fail quiet,
        // the Purchases tab just shows its empty state.
        console.error("Failed to load purchase history:", err);
      } finally {
        if (mounted) setPurchasesLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [clientId, client?.email]);

  const lifetimeSpend = useMemo(
    () => purchases.reduce((s, p) => s + (p.status === "refunded" ? 0 : parseFloat(p.amount || 0)), 0),
    [purchases],
  );

  const handleToggleStar = async () => {
    try {
      const result = await API.users.toggleClientStar(clientId);
      setClient(prev => ({ ...prev, starred: result.starred }));
      toast.success(result.starred ? "Client starred" : "Star removed");
    } catch { toast.error("Failed to update star status"); }
  };

  const campaignsByStatus = useMemo(() => {
    if (!client?.mapped_campaigns) return {};
    return client.mapped_campaigns.reduce((acc, c) => {
      const s = c.status || "Draft";
      if (!acc[s]) acc[s] = [];
      acc[s].push(c);
      return acc;
    }, {});
  }, [client]);

  const refreshClient = async () => {
    try {
      const data = await API.users.getClientDetails(clientId);
      setClient(data);
    } catch (err) { console.error("Failed to refresh client:", err); }
  };

  const pushClientToState = (updated) => {
    if (updated?.client_id === clientId || updated?.id === clientId)
      setClient(prev => ({ ...prev, ...updated }));
  };

  if (loading) {
    return (
      <Backdrop open sx={{ color: "#fff", zIndex: 9999 }}>
        <CircularProgress color="inherit" size={60} />
      </Backdrop>
    );
  }

  if (!client) {
    return (
      <Container maxWidth="md" sx={{ py: 8, textAlign: "center" }}>
        <Typography variant="h5" color="text.secondary">Client not found</Typography>
        <Button variant="contained" onClick={() => navigate("/clients")} sx={{ mt: 3 }}>
          Back to Clients
        </Button>
      </Container>
    );
  }

  const totalValue = client.mapped_campaigns?.reduce((s, c) => s + (c.amount || 0), 0) || 0;

  return (
    <>
      <CssBaseline />
      <HomeHeader />

      {/* ── Hero banner ────────────────────────────────────────────────────── */}
      <Box sx={{
        position: "relative",
        height: { xs: 250, sm: 350 },
        background: `linear-gradient(135deg, ${isDark ? "#1e3a8a" : "#3b82f6"} 0%, ${isDark ? "#7c2d12" : "#ef4444"} 100%)`,
        display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden",
        "&::before": {
          content: '""', position: "absolute", inset: 0,
          background: "radial-gradient(circle at 30% 20%, rgba(255,255,255,0.1) 0%, transparent 50%)",
        },
      }}>
        <Fade in timeout={1000}>
          <Avatar src={client.profilephoto || "/default-avatar.png"} sx={{
            width: { xs: 100, sm: 140 }, height: { xs: 100, sm: 140 },
            border: `6px solid ${isDark ? "#1A1A1A" : "#FFFFFF"}`,
            boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
            position: "relative", zIndex: 2,
          }}>
            {client.full_name?.[0]}
          </Avatar>
        </Fade>
      </Box>

      <Container maxWidth="lg" sx={{ mt: -6, position: "relative", zIndex: 10 }}>

        {/* ── Profile header card ─────────────────────────────────────────── */}
        <Fade in timeout={1200}>
          <Paper elevation={0} sx={{
            borderRadius: 4, p: 4, mb: 4,
            border: `1px solid ${isDark ? "#2A2A2A" : "#F0F0F0"}`,
            backgroundColor: isDark ? "#1A1A1A" : "#FFFFFF",
            backdropFilter: "blur(20px)",
          }}>
            <Box textAlign="center" mb={3}>
              <Box display="flex" alignItems="center" justifyContent="center" gap={2} mb={2}>
                <Typography variant="h3" fontWeight={800} sx={{
                  background: `linear-gradient(135deg, ${isDark ? "#FFFFFF" : "#1A1A1A"} 0%, ${isDark ? "#B0B0B0" : "#666666"} 100%)`,
                  backgroundClip: "text", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
                  fontSize: { xs: "1.8rem", sm: "2.2rem", md: "2.8rem" },
                }}>
                  {client.full_name}
                </Typography>
                <Tooltip title={client.starred ? "Remove star" : "Star client"}>
                  <IconButton onClick={handleToggleStar} size="large">
                    {client.starred
                      ? <StarIcon sx={{ color: "#F59E0B", fontSize: "2rem" }} />
                      : <StarBorderIcon sx={{ fontSize: "2rem" }} />
                    }
                  </IconButton>
                </Tooltip>
              </Box>

              <Stack direction={{ xs: "column", sm: "row" }} spacing={2} justifyContent="center" alignItems="center" mb={3}>
                {client.company && <Chip icon={<BusinessIcon />} label={client.company} sx={{ backgroundColor: isDark ? "#2A2A2A" : "#F8F9FA", fontWeight: 600 }} />}
                {client.location && <Chip icon={<LocationIcon />} label={client.location} sx={{ backgroundColor: isDark ? "#2A2A2A" : "#F8F9FA", fontWeight: 600 }} />}
                {client.is_connected && <Chip icon={<CheckCircleIcon />} label="Connected" color="success" sx={{ fontWeight: 600 }} />}
              </Stack>

              <Stack direction={{ xs: "column", sm: "row" }} spacing={3} justifyContent="center" mb={3}>
                {client.email && (
                  <Box display="flex" alignItems="center" gap={1}>
                    <EmailIcon color="primary" />
                    <Typography variant="body2">{client.email}</Typography>
                  </Box>
                )}
                {client.phone_number && (
                  <Box display="flex" alignItems="center" gap={1}>
                    <PhoneIcon color="primary" />
                    <Typography variant="body2">{client.phone_number}</Typography>
                  </Box>
                )}
              </Stack>

              {client.tags && client.tags.length > 0 && (
                <Stack direction="row" spacing={1} justifyContent="center" flexWrap="wrap" mb={3}>
                  {client.tags.map((tag, idx) => (
                    <Chip key={idx} icon={<TagIcon />} label={tag} size="small" variant="outlined" sx={{ fontWeight: 600 }} />
                  ))}
                </Stack>
              )}

              {client.bio && (
                <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 600, mx: "auto", mb: 3, lineHeight: 1.8 }}>
                  {client.bio}
                </Typography>
              )}

              {/* ── Stats ─────────────────────────────────────────────────── */}
              <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={6} sm={3}>
                  <StatsCard icon={<CampaignIcon />} label="Campaigns" value={client.mapped_campaigns?.length || 0} color="#6366F1" onClick={() => setTab(0)} />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <StatsCard icon={<NoteIcon />} label="Notes" value={client.note_count || 0} color="#10B981" onClick={() => setTab(1)} />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <StatsCard icon={<TrendingUpIcon />} label="Active" value={campaignsByStatus.Launched?.length || 0} color="#F59E0B" />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <StatsCard icon={<MoneyIcon />} label="Total Value" value={`$${totalValue.toLocaleString()}`} color="#8B5CF6" />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <StatsCard
                    icon={<ShoppingBagIcon />}
                    label="Lifetime Spend"
                    value={`$${lifetimeSpend.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                    color="#EC4899"
                    onClick={() => setTab(2)}
                  />
                </Grid>
              </Grid>

              {/* ── Action buttons ─────────────────────────────────────────── */}
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2} justifyContent="center">
                <Button variant="contained" size="large" startIcon={<EditIcon />}
                  onClick={() => setPopup({ type: "edit", client })}
                  sx={{ borderRadius: 3, px: 4, py: 1.5, fontWeight: 700, fontSize: "1.1rem" }}>
                  Edit Client
                </Button>
                <Button variant="outlined" size="large" startIcon={<AddIcon />}
                  onClick={() => setPopup({ type: "notes", client })}
                  sx={{ borderRadius: 3, px: 4, py: 1.5, fontWeight: 700, fontSize: "1.1rem" }}>
                  Add Note
                </Button>
              </Stack>
            </Box>
          </Paper>
        </Fade>

        {/* ── BOUNTY CARD ─────────────────────────────────────────────────── */}
        <BountyCard
          client={client}
          isLoggedIn={userOK}
        />

        {/* ── Content tabs ────────────────────────────────────────────────── */}
        <Paper elevation={0} sx={{
          borderRadius: 4,
          border: `1px solid ${isDark ? "#2A2A2A" : "#F0F0F0"}`,
          backgroundColor: isDark ? "#1A1A1A" : "#FFFFFF",
          overflow: "hidden",
        }}>
          <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="fullWidth" sx={{
            borderBottom: `1px solid ${isDark ? "#2A2A2A" : "#F0F0F0"}`,
            "& .MuiTab-root": { fontWeight: 700, fontSize: "1.1rem", textTransform: "none", py: 3 },
            "& .Mui-selected": { color: "#667eea" },
            "& .MuiTabs-indicator": { backgroundColor: "#667eea", height: 3, borderRadius: "3px 3px 0 0" },
          }}>
            <Tab label={<Box display="flex" alignItems="center" gap={1}><CampaignIcon />Campaigns</Box>} />
            <Tab label={<Box display="flex" alignItems="center" gap={1}><NoteIcon />Notes</Box>} />
            <Tab label={<Box display="flex" alignItems="center" gap={1}><ShoppingBagIcon />Purchases{purchases.length > 0 ? ` (${purchases.length})` : ""}</Box>} />
          </Tabs>

          {/* Campaigns */}
          {tab === 0 && (
            <Box p={4}>
              {client.mapped_campaigns?.length > 0 ? (
                <Grid container spacing={3}>
                  {client.mapped_campaigns.map((campaign, index) => (
                    <Grid key={campaign.campaign_id} item xs={12} md={6} lg={4}>
                      <CampaignCard campaign={campaign} index={index} isDark={isDark} />
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Box textAlign="center" py={8} sx={{
                  backgroundColor: isDark ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.02)",
                  borderRadius: 3, border: `2px dashed ${isDark ? "#404040" : "#E0E0E0"}`,
                }}>
                  <CampaignIcon sx={{ fontSize: 80, color: isDark ? "#666666" : "#CCCCCC", mb: 2 }} />
                  <Typography variant="h6" color="text.secondary" mb={1} fontWeight={600}>No campaigns yet</Typography>
                  <Typography variant="body2" color="text.secondary" mb={3}>Start by creating a campaign for this client</Typography>
                  <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate(`/campaigns/new?client=${clientId}`)}>
                    Create Campaign
                  </Button>
                </Box>
              )}
            </Box>
          )}

          {/* Notes */}
          {tab === 1 && (
            <Box p={4}>
              {client.recent_notes?.length > 0 ? (
                <Stack spacing={3}>
                  {client.recent_notes.map((note, index) => (
                    <NoteCard key={note.id} note={note} index={index} isDark={isDark} />
                  ))}
                  {client.note_count > client.recent_notes.length && (
                    <Box textAlign="center" pt={2}>
                      <Button variant="outlined" onClick={() => setPopup({ type: "notes", client })}>
                        View All {client.note_count} Notes
                      </Button>
                    </Box>
                  )}
                </Stack>
              ) : (
                <Box textAlign="center" py={8} sx={{
                  backgroundColor: isDark ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.02)",
                  borderRadius: 3, border: `2px dashed ${isDark ? "#404040" : "#E0E0E0"}`,
                }}>
                  <NoteIcon sx={{ fontSize: 80, color: isDark ? "#666666" : "#CCCCCC", mb: 2 }} />
                  <Typography variant="h6" color="text.secondary" mb={1} fontWeight={600}>No notes yet</Typography>
                  <Typography variant="body2" color="text.secondary" mb={3}>Add notes to track interactions and important details</Typography>
                  <Button variant="contained" startIcon={<AddIcon />} onClick={() => setPopup({ type: "notes", client })}>
                    Add First Note
                  </Button>
                </Box>
              )}
            </Box>
          )}

          {/* Purchases */}
          {tab === 2 && (
            <Box p={4}>
              {purchasesLoading ? (
                <Box textAlign="center" py={8}><CircularProgress size={32} /></Box>
              ) : purchases.length > 0 ? (
                <Grid container spacing={3}>
                  {purchases.map((purchase, index) => (
                    <Grid key={purchase.order_id} item xs={12} md={6} lg={4}>
                      <PurchaseCard purchase={purchase} index={index} isDark={isDark} />
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Box textAlign="center" py={8} sx={{
                  backgroundColor: isDark ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.02)",
                  borderRadius: 3, border: `2px dashed ${isDark ? "#404040" : "#E0E0E0"}`,
                }}>
                  <ShoppingBagIcon sx={{ fontSize: 80, color: isDark ? "#666666" : "#CCCCCC", mb: 2 }} />
                  <Typography variant="h6" color="text.secondary" mb={1} fontWeight={600}>No purchases yet</Typography>
                  <Typography variant="body2" color="text.secondary" mb={3}>
                    Orders this client places through your Blitz Commerce checkout links — or imports from your
                    online store's sales history — will show up here.
                  </Typography>
                  <Button variant="outlined" startIcon={<LocalMallIcon />} onClick={() => navigate(routes.commerce)}>
                    Go to Blitz Commerce
                  </Button>
                </Box>
              )}
            </Box>
          )}
        </Paper>

        <Box sx={{ pb: 8 }} />
      </Container>

      <ClientPopups
        popup={popup}
        setPopup={setPopup}
        refreshClients={refreshClient}
        pushClientToState={pushClientToState}
      />
    </>
  );
}